import { useRouter } from 'next/router';

import { Checkbox, FieldSet, FormHelp, Label, LabeledControl, Legend } from '@zillow/constellation-10';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { useMultiEditMutation } from '../../../hooks/useMultiEditMutation';

import type { ListingDetailsPatch } from '../../../hooks/queries/listingDetailsCache';

type GenderPreferences = {
  hasNoRoommateGenderPreference: boolean;
  hasRoommateGenderPreferenceFemale: boolean;
  hasRoommateGenderPreferenceMale: boolean;
  hasRoommateGenderPreferenceNonBinary: boolean;
};

const ROOMMATE_GENDER_PREFERENCE = {
  NO_PREFERENCE: 'noPreference',
  FEMALE: 'female',
  MALE: 'male',
  NON_BINARY: 'nonBinary',
} as const;

// The listing stores the checked choices as a list, for example ['female', 'nonBinary'].
export const toGenderList = (flags: Partial<GenderPreferences> = {}) => [
  ...(flags.hasNoRoommateGenderPreference ? [ROOMMATE_GENDER_PREFERENCE.NO_PREFERENCE] : []),
  ...(flags.hasRoommateGenderPreferenceFemale ? [ROOMMATE_GENDER_PREFERENCE.FEMALE] : []),
  ...(flags.hasRoommateGenderPreferenceMale ? [ROOMMATE_GENDER_PREFERENCE.MALE] : []),
  ...(flags.hasRoommateGenderPreferenceNonBinary ? [ROOMMATE_GENDER_PREFERENCE.NON_BINARY] : []),
];

const toGenderFlags = (genderList: Array<string>): GenderPreferences => ({
  hasNoRoommateGenderPreference: genderList.includes(ROOMMATE_GENDER_PREFERENCE.NO_PREFERENCE),
  hasRoommateGenderPreferenceFemale: genderList.includes(ROOMMATE_GENDER_PREFERENCE.FEMALE),
  hasRoommateGenderPreferenceMale: genderList.includes(ROOMMATE_GENDER_PREFERENCE.MALE),
  hasRoommateGenderPreferenceNonBinary: genderList.includes(ROOMMATE_GENDER_PREFERENCE.NON_BINARY),
});

// One patch per saved flag, so a `/multi` rejection of one flag drops only that flag's change.
export const genderPreferencePatches = (flags: object = {}): Array<ListingDetailsPatch> =>
  Object.entries(flags).map(([field, isChecked]) => ({
    field,
    apply: (cached) => {
      const requirements = cached.roomForRent?.roommateRequirements;
      if (!requirements) {
        return cached;
      }

      const cachedFlags = toGenderFlags(requirements.roommateGenderPreferences ?? []);

      return {
        ...cached,
        roomForRent: {
          ...cached.roomForRent,
          roommateRequirements: {
            ...requirements,
            roommateGenderPreferences: toGenderList({ ...cachedFlags, [field]: Boolean(isChecked) }),
          },
        },
      };
    },
  }));

const GenderPreferenceFormField = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const roommateGenderPreferences = listingDetails?.roomForRent?.roommateRequirements?.roommateGenderPreferences ?? [];

  const updateMutation = useMultiEditMutation({
    applyToCache: ({ postData }) => genderPreferencePatches(postData.property.roomForRent),
  });

  const genderPreferencesField = useFieldSave({
    fieldName: 'genderPreferences',
    initialValue: toGenderFlags(roommateGenderPreferences),
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: {
        property: {
          listingAlias,
          roomForRent: {
            hasNoRoommateGenderPreference: value.hasNoRoommateGenderPreference,
            hasRoommateGenderPreferenceFemale: value.hasRoommateGenderPreferenceFemale,
            hasRoommateGenderPreferenceMale: value.hasRoommateGenderPreferenceMale,
            hasRoommateGenderPreferenceNonBinary: value.hasRoommateGenderPreferenceNonBinary,
          },
        },
      },
    }),
    autoSaveOnChange: true,
  });

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    const key = name as keyof GenderPreferences;
    const value = checked;

    let newPreferences = {
      ...genderPreferencesField.value,
      [key]: value,
    };

    // Handle "No preference" mutual exclusivity
    if (key === 'hasNoRoommateGenderPreference') {
      if (newPreferences.hasNoRoommateGenderPreference) {
        // Setting "No preference" to true, clear all others
        newPreferences = {
          hasNoRoommateGenderPreference: true,
          hasRoommateGenderPreferenceFemale: false,
          hasRoommateGenderPreferenceMale: false,
          hasRoommateGenderPreferenceNonBinary: false,
        };
      } else {
        // Unchecking "No preference" - do nothing (will be caught by validation)
        newPreferences.hasNoRoommateGenderPreference = false;
      }
    } else {
      // If any specific preference is selected, clear "No preference"
      if (newPreferences[key]) {
        newPreferences.hasNoRoommateGenderPreference = false;
      }

      // If all specific preferences are false, default to "No preference"
      const hasAnySpecificPreference =
        newPreferences.hasRoommateGenderPreferenceFemale ||
        newPreferences.hasRoommateGenderPreferenceMale ||
        newPreferences.hasRoommateGenderPreferenceNonBinary;
      if (!hasAnySpecificPreference) {
        newPreferences.hasNoRoommateGenderPreference = true;
      }
    }

    genderPreferencesField.onChange(newPreferences);
  };

  const error = genderPreferencesField.error;

  return (
    <FieldSet
      data-testid="GenderPreferenceFormField"
      legend={<Legend>Gender preference</Legend>}
      error={!!error}
      description={<FormHelp>{error}</FormHelp>}
    >
      <LabeledControl
        label={<Label>No preference</Label>}
        control={
          <Checkbox
            name="hasNoRoommateGenderPreference"
            checked={genderPreferencesField.value.hasNoRoommateGenderPreference}
            onChange={handleCheckboxChange}
          />
        }
      />

      <LabeledControl
        label={<Label>Female</Label>}
        control={
          <Checkbox
            name="hasRoommateGenderPreferenceFemale"
            checked={genderPreferencesField.value.hasRoommateGenderPreferenceFemale}
            onChange={handleCheckboxChange}
          />
        }
      />

      <LabeledControl
        label={<Label>Male</Label>}
        control={
          <Checkbox
            name="hasRoommateGenderPreferenceMale"
            checked={genderPreferencesField.value.hasRoommateGenderPreferenceMale}
            onChange={handleCheckboxChange}
          />
        }
      />

      <LabeledControl
        label={<Label>Non-binary</Label>}
        control={
          <Checkbox
            name="hasRoommateGenderPreferenceNonBinary"
            checked={genderPreferencesField.value.hasRoommateGenderPreferenceNonBinary}
            onChange={handleCheckboxChange}
          />
        }
      />
    </FieldSet>
  );
};

export default GenderPreferenceFormField;
